import { type FetchResult, useMutation } from '@apollo/client';
import { hasAnyTranslation } from '@assets/i18n/config';
import {
  type AssetClassLevel,
  FinancialProductStrategy,
} from '@generated/graphql';
import type {
  AssetClassBranch,
  TargetAllocation,
} from '@interfaces/financialProduct';

import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import { useToast } from 'hephaestus/web';
import { pick, round, sumBy } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import type { AssetClassTargetAllocation } from '../addModelPortfoliosIPSAndAssetClasses/assetClassTargetAllocation.types';
import type { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio.types';
import {
  CREATE_MODEL_PORTFOLIO,
  TRANSITION_MODEL_PORTFOLIO,
  UPDATE_MODEL_PORTFOLIO,
} from '../queries';

export type FormattedChild = {
  financialProductId: string;
  percentage: number;
  isCashEquivalent: boolean;
  taxRank?: number;
};

export type FormattedAssetClassBranch = {
  assetClassId: string;
  targetPercentage: number;
};

/**
 * Returns true when the branch list is invalid for saving:
 * - no branches, any branch missing an asset class, or total ≠ 100%.
 * Shared between the Details and Securities steps so the rule stays in one place.
 */
export const isBranchAllocationInvalid = (
  branches: AssetClassBranch[],
): boolean => {
  if (branches.length === 0) return true;
  if (branches.some((b) => !b.assetClass?.id || b.targetPercentage == null))
    return true;
  return round(sumBy(branches, 'targetPercentage'), 2) !== 100;
};

export const formatAssetClassBranches = (
  branches: AssetClassBranch[],
): FormattedAssetClassBranch[] =>
  branches
    .filter((b): b is AssetClassBranch & { assetClass: { id: string } } =>
      Boolean(b.assetClass?.id),
    )
    .map((b) => ({
      assetClassId: b.assetClass.id,
      targetPercentage: b.targetPercentage ?? 0,
    }));

export type FormattedTargetAllocation = {
  secondaryAssetClassId: string;
  min: number;
  max: number;
};

export const formatChildren = (
  securities: Security[],
  isAssetClass = false,
): FormattedChild[] => {
  if (!securities?.length) return [];

  return securities
    .filter((security) => !security.financialProduct.isCash)
    .map((security, index) => {
      const formattedChild: FormattedChild = {
        financialProductId: security.financialProduct.id?.startsWith('temp-')
          ? ''
          : (security.financialProduct.id ?? ''),
        percentage: security.percentage,
        isCashEquivalent: security.isCashEquivalent ?? false,
      };

      if (isAssetClass) {
        formattedChild.taxRank = security.taxRank ?? index + 1;
      }

      return formattedChild;
    });
};

export const formatTargetAllocations = (
  targetAllocations: TargetAllocation[],
): FormattedTargetAllocation[] =>
  targetAllocations.map((targetAllocation) => ({
    secondaryAssetClassId: targetAllocation.secondaryAssetClass.id ?? '',
    min: targetAllocation.min,
    max: targetAllocation.max,
  }));

const updateModelPortfolioState = (
  modelPortfolioDetails: ModelPortfolioData,
  modelPortfolioId: string,
  securities?: Security[],
  targetAllocations?: AssetClassTargetAllocation[],
  zeroPercentCash?: boolean,
): ModelPortfolioData => ({
  ...modelPortfolioDetails,
  id: modelPortfolioId,
  ...(securities === undefined ? {} : { securities }),
  ...(targetAllocations === undefined ? {} : { targetAllocations }),
  ...(zeroPercentCash === undefined ? {} : { zeroPercentCash }),
});

const buildAssetClassFields = (
  strategy: FinancialProductStrategy | string | undefined,
  assetClassBranches?: AssetClassBranch[],
  assetClassLevel?: AssetClassLevel,
): Partial<{
  assetClassBranches: FormattedAssetClassBranch[];
  assetClassLevel: AssetClassLevel;
}> => {
  if (strategy !== FinancialProductStrategy.AssetClass) return {};
  if (!assetClassLevel) return {};
  return {
    assetClassBranches: assetClassBranches?.length
      ? formatAssetClassBranches(assetClassBranches)
      : [],
    assetClassLevel,
  };
};

/** Whitelist for `updateModelPortfolio` payload; client-only fields are omitted by not listing them here. */
const MP_SCALAR_KEYS = [
  'strategy',
  'suggestable',
  'themeId',
  'zeroPercentCash',
  // Scalars, deliberately not MP_NUMBER_KEYS: pickValidNumbers drops null,
  // which is exactly the value that clears an override back to inherit.
  'driftCalculationMethod',
  'driftTolerancePercent',
] as const;

const MP_TRANSLATED_KEYS = [
  'translatedName',
  'riskProfileName',
  'translatedPortfolioDescription',
  'translatedInvestmentObjective',
  'translatedPortfolioDisclaimer',
  'translatedLiquidity',
  'translatedUrl',
] as const;

const MP_NUMBER_KEYS = [
  'forecastedRateOfReturn',
  'forecastedStandardDeviation',
  'suggestedMinScore',
  'suggestedMaxScore',
  'lowThresholdScore',
  'riskReturnLevel',
] as const;

const pickScalars = (mp: ModelPortfolioData) =>
  Object.fromEntries(
    Object.entries(pick(mp, [...MP_SCALAR_KEYS])).filter(
      ([, v]) => v !== undefined,
    ),
  );

const pickValidTranslations = (mp: ModelPortfolioData) =>
  Object.fromEntries(
    MP_TRANSLATED_KEYS.flatMap((k) => {
      const v = mp[k];
      if (!v || !hasAnyTranslation(v)) return [];
      return [[k, v]];
    }),
  );

const pickValidNumbers = (mp: ModelPortfolioData) =>
  Object.fromEntries(
    MP_NUMBER_KEYS.flatMap((k) => {
      const v = mp[k];
      if (v == null || Number.isNaN(v)) return [];
      return [[k, v]];
    }),
  );

const buildSharedFields = (mp: ModelPortfolioData) => ({
  ...pickScalars(mp),
  ...pickValidTranslations(mp),
  ...pickValidNumbers(mp),
  children: formatChildren(
    mp.securities ?? [],
    mp.strategy === FinancialProductStrategy.AssetClass,
  ),
  targetAllocations: mp.targetAllocations?.length
    ? formatTargetAllocations(mp.targetAllocations)
    : [],
  ...buildAssetClassFields(
    mp.strategy,
    mp.assetClassBranches,
    mp.assetClassLevel,
  ),
});

/** When `organizationId` is omitted, product-service resolves organization from the authenticated user. */
const buildCreateInput = (mp: ModelPortfolioData) => ({
  ...(mp.organizationId ? { organizationId: mp.organizationId } : {}),
  ...buildSharedFields(mp),
});

const buildUpdateInput = (
  mp: ModelPortfolioData,
  modelPortfolioId: string,
) => ({
  modelPortfolioId,
  ...buildSharedFields(mp),
});

export const useSaveModelPortfolio = () => {
  const { t } = useTranslation(['modelPortfolios']);
  const { enqueueToast } = useToast();
  const [createModelPortfolio, { loading: createLoading }] = useMutation(
    CREATE_MODEL_PORTFOLIO,
  );
  const [updateModelPortfolio, { loading: updateLoading }] = useMutation(
    UPDATE_MODEL_PORTFOLIO,
  );
  const [transitionModelPortfolio, { loading: transitionLoading }] =
    useMutation(TRANSITION_MODEL_PORTFOLIO);

  const saveModelPortfolio = async (
    modelPortfolioDetails: ModelPortfolioData,
    transitionToActive?: boolean,
  ) => {
    let modelPortfolioId = modelPortfolioDetails?.id ?? '';

    try {
      if (
        modelPortfolioDetails.strategy ===
          FinancialProductStrategy.AssetClass &&
        !modelPortfolioDetails.assetClassLevel
      ) {
        throw new Error(t('modelPortfolios:portfolio.missingAssetClassLevel'));
      }

      let result: FetchResult;

      if (modelPortfolioId) {
        result = await updateModelPortfolio({
          variables: {
            input: buildUpdateInput(modelPortfolioDetails, modelPortfolioId),
          },
        });

        if (!result?.data?.updateModelPortfolio?.modelPortfolio) {
          throw new Error(t('modelPortfolios:portfolio.updateError'));
        }
      } else {
        result = await createModelPortfolio({
          variables: {
            input: buildCreateInput(modelPortfolioDetails),
          },
        });

        if (!result?.data?.createModelPortfolio?.modelPortfolio?.id) {
          throw new Error(t('modelPortfolios:portfolio.createError'));
        }

        modelPortfolioId = result.data.createModelPortfolio.modelPortfolio.id;
      }

      const updatedModelPortfolio = updateModelPortfolioState(
        modelPortfolioDetails,
        modelPortfolioId,
        modelPortfolioDetails.securities,
        modelPortfolioDetails.targetAllocations,
        modelPortfolioDetails.zeroPercentCash,
      );

      const transitionResult =
        transitionToActive && modelPortfolioId
          ? await transitionModelPortfolio({
              variables: {
                input: {
                  modelPortfolioId,
                  transition: 'activate',
                },
              },
            })
          : undefined;

      if (
        transitionToActive &&
        modelPortfolioId &&
        !transitionResult?.data?.transitionModelPortfolio?.modelPortfolio
      ) {
        throw new Error(
          'Failed to transition model portfolio - no data returned from transition mutation',
        );
      }

      enqueueToast(
        transitionResult
          ? t('modelPortfolios:portfolio.createActiveSuccess')
          : t('modelPortfolios:portfolio.draftSaveSuccess'),
        { variant: 'success' },
      );

      return updatedModelPortfolio;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('modelPortfolios:portfolio.saveError');
      enqueueToast(errorMessage, { variant: 'error' });
      throw error;
    }
  };

  return {
    saveModelPortfolio,
    loading: createLoading || updateLoading || transitionLoading,
  };
};
